import styled from "@emotion/styled";
import redmarker from "../../assets/images/redmarker.svg";
import orangemarker from "../../assets/images/orangemarker.svg";
import SpinnerForMap from "../spinnerForMap";
import theme from "../../theme";
import {
  AccidentMarker,
  BubbleTailIcon,
  CopyButton,
  DangerousDrivingMarker,
  IdleTimeMarker,
  WarningBatteryMarker,
  WarningEscapeMarker,
} from "../../assets/icons/icons";
import { UnifiedMap, UnifiedMarker, UnifiedOverlay, UnifiedPolyline } from "../map";
import CustomMarkers from "../customMarkers";
import {
  checkRouteRadius,
  copyToClipboard,
  formatDate,
  handleKeyDown,
} from "../../utils/util";
import { MobilityReportByUser, RouteInfo } from "../../types/types";
import { useMapProvider } from "../../hooks/useMapProvider";
import { useEffect, useState } from "react";
import { getRoadAddress } from "../../api/detect";
import userStorage from "../../utils/userStorage";
import { useSnackbar } from "notistack";
import { DotLoader } from "../dotLoader";
import { useI18n } from "../../locales";
import { getEventTypeKey } from "../../utils/langUtils";

export const MAPTYPE = [
  // 이름은 렌더링 시 i18n으로 대체됨
  { name: "normal", value: 1 },
  { name: "satellite", value: 3 },
];

type Prop = {
  routeData: RouteInfo[] | undefined;
  routeDataIsLoading: boolean;
  center: {
    lat: number;
    lng: number;
  };
  setCenter: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  mapLevel: number;
  setMapLevel: React.Dispatch<React.SetStateAction<number>>;
  selectedReport: MobilityReportByUser | null;
  reportData: MobilityReportByUser[] | undefined;
  isPast: boolean | undefined;
  isNotInitialValue: boolean;
  setNoReportData: React.Dispatch<React.SetStateAction<boolean>>;
};
export const RouteMap = ({
  routeData,
  routeDataIsLoading,
  center,
  setCenter,
  mapLevel,
  setMapLevel,
  selectedReport,
  reportData,
  setNoReportData,
}: Prop) => {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const user = userStorage.get();
  // const [map, setMap] = useState<kakao.maps.Map>();
  const [mapTypeId, setMapTypeId] = useState(1);
  const [markerFilterList, setMarkerFilterList] = useState<string[]>([]);
  const [markerData, setMarkerData] = useState<RouteInfo[]>();
  const [eventAddress, setEventAddress] = useState("");
  
  // 맵 제공자를 state로 관리하고 이전 center 값을 저장하여 무한 루프 방지
  const currentMapProvider = useMapProvider(center.lat, center.lng, mapLevel).mapProvider;
  const [mapProvider, setMapProvider] = useState(currentMapProvider);
  const [lastCenter, setLastCenter] = useState(center);
  
  // mapProvider가 실제로 변경될 때만 업데이트 (center가 충분히 이동했을 때만)
  useEffect(() => {
    // center가 의미있게 변경되었는지 확인 (소수점 3자리, 약 100m)
    const latDiff = Math.abs(center.lat - lastCenter.lat);
    const lngDiff = Math.abs(center.lng - lastCenter.lng);
    const hasMovedSignificantly = latDiff > 0.001 || lngDiff > 0.001;
    
    if (currentMapProvider !== mapProvider && hasMovedSignificantly) {
      console.log(`🔄 지도 제공자 변경: ${mapProvider} → ${currentMapProvider}`);
      
      // OSM에서 Kakao로 전환 시 선택된 이벤트 초기화
      if (mapProvider === 'osm' && currentMapProvider === 'kakao') {
        setSelectedEvent("");
        setEventAddress("");
      }
      
      // 지도 제공자 즉시 변경 (지도는 바로 표시)
      setMapProvider(currentMapProvider);
      setLastCenter(center);
    }
  }, [currentMapProvider, mapProvider, center, lastCenter]);
  const [eventAddressIsLoading, setEventAddressIsLoading] =
    useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");

  // 지도에서 마커 필터 아이콘 클릭
  const handleClickMarkerFilter = (filter: string) => {
    // 필터 배열에 선택된 필터가 없는 경우
    if (!markerFilterList.includes(filter)) {
      setMarkerFilterList((prev) => [...prev, filter]);
    }
    // 필터 배열에 선택된 필터가 있는 경우
    else if (markerFilterList.includes(filter)) {
      setMarkerFilterList((prev) => prev.filter((el) => el !== filter));
    }
  };

  // 지도 타입 토글 버튼 클릭
  const handleClickMapType = (el: { name: string; value: number }) => {
    setMapTypeId(el.value);
  };

  // 이벤트 발생 지점의 주소 구하기
  useEffect(() => {
    if (typeof routeData !== "undefined" && routeData.length !== 0) {
      if (selectedEvent !== "") {
        setEventAddressIsLoading(true);
        const targetIndex = routeData.findIndex(
          (el) => el.registered_at === selectedEvent
        );

        const getRoad = async () => {
          const result = await getRoadAddress({
            lng: routeData[targetIndex].longitude,
            lat: routeData[targetIndex].latitude,
          });
          setEventAddressIsLoading(false);
          setEventAddress(result?.result?.result || "주소 조회 실패");
        };

        getRoad();
      }
    }
  }, [selectedEvent, routeData]);

  // 지도 중심 좌표 이동
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    // 선택된 리포트 데이터 (최근 운행내역)가 있는 경우
    if (selectedReport) {
      let existingCenterCoords =
        selectedReport.center_latitude && selectedReport.center_longitude;
      let existingStartCoords =
        selectedReport.start_latitude && selectedReport.start_longitude;
      let existingEndCoords =
        selectedReport.end_latitude && selectedReport.end_longitude;

      // 중심 좌표가 있음
      if (existingCenterCoords) {
        timeoutId = setTimeout(() => {
          setCenter({
            lat: selectedReport.center_latitude,
            lng: selectedReport.center_longitude,
          });
        }, 500);
        checkRouteRadius(selectedReport.route_radius, setMapLevel);
      }
      // 중심좌표가 없고 시작점 끝점은 있을 때 - 시작점 좌표와 끝점 좌표의 중간값이 센터
      else if (
        !existingCenterCoords &&
        existingStartCoords &&
        existingEndCoords
      ) {
        setMapLevel(1);
        timeoutId = setTimeout(() => {
          setCenter({
            lat:
              (selectedReport.start_latitude +
                selectedReport.end_latitude) /
              2,
            lng:
              (selectedReport.start_longitude +
                selectedReport.end_longitude) /
              2,
          });
        }, 500);
      }

      // 시작점이 없고 끝점만 있을때 - 중심은 끝점
      else if (
        !existingCenterCoords &&
        !existingStartCoords &&
        existingEndCoords
      ) {
        setMapLevel(1);
        timeoutId = setTimeout(() => {
          setCenter({
            lat: selectedReport.end_latitude,
            lng: selectedReport.end_longitude,
          });
        }, 500);
      }

      // 시작점이 있고 끝점만 없을때 - 중심은 시작점
      else if (
        !existingCenterCoords &&
        !existingEndCoords &&
        existingStartCoords
      ) {
        setMapLevel(1);
        timeoutId = setTimeout(() => {
          setCenter({
            lat: selectedReport.start_latitude,
            lng: selectedReport.start_longitude,
          });
        }, 500);
      }

      // 둘다 없을때
      else if (
        !existingCenterCoords &&
        !existingStartCoords &&
        !existingEndCoords
      ) {
        setMapLevel(13);
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(user?.latitude),
            lng: Number(user?.longitude),
          });
        }, 500);

        // 선택된 리포트 데이터에 모든 좌표를 확인할 수 없을 때
        if (selectedReport) {
          setNoReportData(true);
          timeoutId = setTimeout(() => {
            setNoReportData(false);
          }, 1000);
        }
      }
    }
    // 선택된 리포트 데이터 (최근 운행내역) 데이터가 없는 경우
    if (!selectedReport) {
      setCenter({
        lat: Number(user?.latitude),
        lng: Number(user?.longitude),
      });
      setMapLevel(13);
    }

    return () => timeoutId && clearTimeout(timeoutId);
  }, [reportData, selectedReport]);

  // 언어팩 키를 현재 언어의 값으로 변환하는 함수
  const getEventTypeValue = (key: string): string => {
    const keyMap: Record<string, string> = {
      "batteryLowEvent": t.control.batteryLowEvent,
      "areaExitEvent": t.control.areaExitEvent,
      "dangerousDrivingEvent": t.control.dangerousDrivingEvent,
      "vehicleRolloverEvent": t.control.vehicleRolloverEvent,
      "engineAbandonmentEvent": t.control.engineAbandonmentEvent,
      "dangerousAreaEntryEvent": t.control.dangerousAreaEntryEvent,
      "theftDetection": t.control.theftDetection,
    };
    return keyMap[key] || key;
  };

  useEffect(() => {
    // 마커필터 초기값 설정
    const eventTypeKeys = [
      "batteryLowEvent",
      "areaExitEvent",
      "dangerousDrivingEvent",
      "vehicleRolloverEvent",
      "engineAbandonmentEvent",
    ];
    const eventKeysToAdd: string[] = [];
    
    // routeData가 있으면 routeData 기반으로 이벤트 확인
    if (routeData && routeData.length > 0) {
      const checkEvent = (eventKey: string) => {
        const eventValue = getEventTypeValue(eventKey);
        // 백엔드에서 받은 값과 언어팩 값을 모두 비교
        if (routeData.filter((el) => {
          const backendValue = el.event_type;
          const convertedKey = getEventTypeKey(backendValue, t);
          return convertedKey === eventKey || backendValue === eventValue;
        }).length > 0) {
          if (!eventKeysToAdd.includes(eventKey)) {
            eventKeysToAdd.push(eventKey);
          }
        }
      };
      eventTypeKeys.forEach((el) => checkEvent(el));
    }
    
    // routeData가 없거나 routeData에 없는 이벤트는 selectedReport.events에서 확인
    if (selectedReport && selectedReport.events) {
      selectedReport.events.forEach((event) => {
        const eventKey = getEventTypeKey(event, t);
        // "normal"이 아닌 경우만 추가
        if (eventKey && eventKey !== "normal" && !eventKeysToAdd.includes(eventKey)) {
          eventKeysToAdd.push(eventKey);
        }
      });
    }
    
    // 필터 리스트 업데이트
    if (eventKeysToAdd.length > 0) {
      setMarkerFilterList(eventKeysToAdd);
    } else {
      setMarkerFilterList([]);
    }
  }, [routeData, selectedReport, t]);

  // 마커필터 상태에 따라 지도에 출력될 마커 리스트 관리
  useEffect(() => {
    if (!routeData) setMarkerData([]); // 경로 데이터 없으면 마커 데이터 지우기
    // 선택된 마커필터가 없는 경우
    if (routeData && markerFilterList.length === 0) {
      setMarkerData([]);
      return;
    }
    // 선택된 마커필터가 있는 경우
    if (routeData && markerFilterList.length > 0) {
      setMarkerData(
        routeData?.filter((el) => {
          const backendValue = el.event_type;
          const convertedKey = getEventTypeKey(backendValue, t);
          return markerFilterList.some((markerKey) => {
            const markerValue = getEventTypeValue(markerKey);
            return convertedKey === markerKey || backendValue === markerValue;
          });
        })
      );
      return;
    }
  }, [routeData, markerFilterList, selectedReport, t]);

  // 선택된 데이터가 바뀌면 이벤트 오버레이 끄기
  useEffect(() => {
    setSelectedEvent("");
  }, [selectedReport]);

  return (
    <>
      <WrapperStyledDiv style={{ position: "relative" }}>
        {/* 지도 로딩스피너 */}
        {routeDataIsLoading && (
          <SpinnerContainerStyledDiv>
            {
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <SpinnerForMap />
                <p
                  style={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "x-large",
                    textShadow: "0px 0px 7px rgba(0,0,0,0.9)",
                  }}
                >
                  조회 중입니다.
                </p>
              </div>
            }
          </SpinnerContainerStyledDiv>
        )}

        {/* 마커 필터 버튼 */}
        <ButtonsContainerStyledDiv>
          {/* 배터리부족 */}
          <MarkerFilterStyledButton
            isBlock={
              !!(routeData &&
              routeData.filter((el) => {
                const backendValue = el.event_type;
                const convertedKey = getEventTypeKey(backendValue, t);
                return convertedKey === "batteryLowEvent" || backendValue === getEventTypeValue("batteryLowEvent");
              }).length > 0) ||
              !!(selectedReport?.events &&
              selectedReport.events.some((event) => {
                const eventKey = getEventTypeKey(event, t);
                return eventKey === "batteryLowEvent";
              }))
            }
            isActive={markerFilterList.includes("batteryLowEvent")}
            onClick={() => handleClickMarkerFilter("batteryLowEvent")}
          >
            <WarningBatteryMarker width={"45px"} height={"45px"} />
            <span>{t.control.batteryLowEvent}</span>
          </MarkerFilterStyledButton>

          {/* 사용지역이탈 */}
          <MarkerFilterStyledButton
            isBlock={
              !!(routeData &&
              routeData.filter((el) => {
                const backendValue = el.event_type;
                const convertedKey = getEventTypeKey(backendValue, t);
                return convertedKey === "areaExitEvent" || backendValue === getEventTypeValue("areaExitEvent");
              }).length > 0) ||
              !!(selectedReport?.events &&
              selectedReport.events.some((event) => {
                const eventKey = getEventTypeKey(event, t);
                return eventKey === "areaExitEvent";
              }))
            }
            isActive={markerFilterList.includes("areaExitEvent")}
            onClick={() => handleClickMarkerFilter("areaExitEvent")}
          >
            <WarningEscapeMarker width={"45px"} height={"45px"} />
            <span>{t.control.areaExitEvent}</span>
          </MarkerFilterStyledButton>

          {/* 시동방치 */}
          <MarkerFilterStyledButton
            isBlock={
              !!(routeData &&
              routeData.filter((el) => {
                const backendValue = el.event_type;
                const convertedKey = getEventTypeKey(backendValue, t);
                return convertedKey === "engineAbandonmentEvent" || backendValue === getEventTypeValue("engineAbandonmentEvent");
              }).length > 0) ||
              !!(selectedReport?.events &&
              selectedReport.events.some((event) => {
                const eventKey = getEventTypeKey(event, t);
                return eventKey === "engineAbandonmentEvent";
              }))
            }
            isActive={markerFilterList.includes("engineAbandonmentEvent")}
            onClick={() => handleClickMarkerFilter("engineAbandonmentEvent")}
          >
            <IdleTimeMarker width={"45px"} height={"45px"} />
            <span>{t.control.engineAbandonmentEvent}</span>
          </MarkerFilterStyledButton>

          {/* 위험운전 */}
          <MarkerFilterStyledButton
            isBlock={
              !!(routeData &&
              routeData.filter((el) => {
                const backendValue = el.event_type;
                const convertedKey = getEventTypeKey(backendValue, t);
                return convertedKey === "dangerousDrivingEvent" || backendValue === getEventTypeValue("dangerousDrivingEvent");
              }).length > 0) ||
              !!(selectedReport?.events &&
              selectedReport.events.some((event) => {
                const eventKey = getEventTypeKey(event, t);
                return eventKey === "dangerousDrivingEvent";
              }))
            }
            isActive={markerFilterList.includes("dangerousDrivingEvent")}
            onClick={() => handleClickMarkerFilter("dangerousDrivingEvent")}
          >
            <DangerousDrivingMarker width={"45px"} height={"45px"} />
            <span>{t.control.dangerousDrivingEvent}</span>
          </MarkerFilterStyledButton>

          {/* 차량전복 */}
          <MarkerFilterStyledButton
            isBlock={
              !!(routeData &&
              routeData.filter((el) => {
                const backendValue = el.event_type;
                const convertedKey = getEventTypeKey(backendValue, t);
                return convertedKey === "vehicleRolloverEvent" || backendValue === getEventTypeValue("vehicleRolloverEvent");
              }).length > 0) ||
              !!(selectedReport?.events &&
              selectedReport.events.some((event) => {
                const eventKey = getEventTypeKey(event, t);
                return eventKey === "vehicleRolloverEvent";
              }))
            }
            isActive={markerFilterList.includes("vehicleRolloverEvent")}
            onClick={() => handleClickMarkerFilter("vehicleRolloverEvent")}
          >
            <AccidentMarker width={"45px"} height={"45px"} />
            <span>{t.control.vehicleRolloverEvent}</span>
          </MarkerFilterStyledButton>
        </ButtonsContainerStyledDiv>

        {/* 지도 타입 전환 버튼 (지도 위에 오버레이) */}
        <MapTypeToggleWrapperStyledDiv>
          {/* 일반 | 위성 버튼 */}
          {MAPTYPE.map((el, idx) => (
            <MapTypeBoxStyledDiv
              key={idx.toString()}
              onClick={() => handleClickMapType(el)}
              isSelected={el.value === mapTypeId}
              isFirst={idx === 0}
              role="button"
              tabIndex={0}
              onKeyDown={handleKeyDown}
            >
              {el.name === "normal" ? t.control.normal : t.control.satellite}
            </MapTypeBoxStyledDiv>
          ))}
        </MapTypeToggleWrapperStyledDiv>

        {/* 지도 */}
        <UnifiedMap
          minLevel={0}
          // onCreate={(map) => setMap(map)}
          center={{
            lat: Number(center.lat),
            lng: Number(center.lng),
          }}
          style={{ width: "100%", height: "100%" }}
          level={mapLevel}
          onClick={() => {
            setSelectedEvent("");
            setEventAddress("");
          }}
          mapTypeId={mapTypeId}
          onIdle={(target) => {
            // center가 의미있게 변경되었을 때만 업데이트 (무한 루프 방지)
            const newLat = target.getCenter().getLat();
            const newLng = target.getCenter().getLng();
            const latDiff = Math.abs(newLat - center.lat);
            const lngDiff = Math.abs(newLng - center.lng);
            
            if (latDiff > 0.0001 || lngDiff > 0.0001) {
              setCenter({
                lat: newLat,
                lng: newLng,
              });
            }
            setMapLevel(target.getLevel());
          }}
        >

          {/* 경로 표시 부분 */}
          {!routeDataIsLoading &&
            routeData?.length === 0 &&
            selectedReport &&
            selectedReport.start_latitude &&
            selectedReport.end_latitude && (
              <UnifiedPolyline
                path={[
                  {
                    lat: selectedReport.start_latitude,
                    lng: selectedReport.start_longitude,
                  },
                  {
                    lat: selectedReport.end_latitude,
                    lng: selectedReport.end_longitude,
                  },
                ]}
                strokeWeight={14} // 선의 두께 입니다
                strokeColor={
                  selectedReport?.events?.some((el) =>
                    getEventTypeKey(el, t) === "vehicleRolloverEvent"
                  )
                    ? `${theme.color.red_500}`
                    : selectedReport?.events?.some((el) => {
                        const eventKey = getEventTypeKey(el, t);
                        return [
                          "dangerousAreaEntryEvent",
                          "areaExitEvent",
                          "batteryLowEvent",
                          "dangerousDrivingEvent",
                          "engineAbandonmentEvent",
                        ].includes(eventKey);
                      })
                    ? `${theme.color.orange_500}`
                    : `${theme.color.green_500}`
                }
                strokeOpacity={0.7} // 선의 불투명도 입니다 1에서 0 사이의 값이며 0에 가까울수록 투명합니다
                strokeStyle={"solid"} // 선의 스타일입니다
              />
            )}
          {!routeDataIsLoading && routeData && (
            <>
              <UnifiedPolyline
                path={routeData?.map((el) => {
                  return { lat: el.latitude, lng: el.longitude };
                })}
                strokeWeight={14} // 선의 두께 입니다
                strokeColor={
                  // routeData.some((el) =>
                  //   ["차량전복", "사용지역이탈", "배터리부족"].includes(
                  //     el.event_type
                  //   )
                  // )
                  //   ? `${theme.color.red_500}`
                  //   : routeData.some((el) =>
                  //       ["위험지역진입"].includes(el.event_type)
                  //     )
                  //   ? `${theme.color.orange_500}`
                  //   : `${theme.color.green_500}`
                  selectedReport?.events?.some((el) =>
                    getEventTypeKey(el, t) === "vehicleRolloverEvent"
                  )
                    ? `${theme.color.red_500}`
                    : selectedReport?.events?.some((el) => {
                        const eventKey = getEventTypeKey(el, t);
                        return [
                          "dangerousAreaEntryEvent",
                          "areaExitEvent",
                          "batteryLowEvent",
                          "dangerousDrivingEvent",
                          "engineAbandonmentEvent",
                        ].includes(eventKey);
                      })
                    ? `${theme.color.orange_500}`
                    : `${theme.color.green_500}`
                }
                strokeOpacity={0.7} // 선의 불투명도 입니다 1에서 0 사이의 값이며 0에 가까울수록 투명합니다
                strokeStyle={"solid"} // 선의 스타일입니다
              />

              {CustomMarkers(
                routeData,
                selectedReport
                // reportData,
                // isPast,
                // isNotInitialValue
              )}
            </>
          )}

          {/* 마커 부분 */}
          {
            // routeData
            markerData
              ?.filter((el) => {
                // "정상" 또는 "normal"이 아닌 경우만 표시
                const eventKey = getEventTypeKey(el.event_type, t);
                return eventKey !== "normal" && el.event_type !== "정상";
              })
              .map((elel, index) => (
                <div key={index.toString()}>
                  <UnifiedMarker
                    zIndex={
                      getEventTypeKey(elel.event_type, t) === "vehicleRolloverEvent"
                        ? theme.zindex.redMarker
                        : theme.zindex.orangeMarker
                    }
                    clickable={true}
                    onClick={() => setSelectedEvent(elel.registered_at)}
                    position={{
                      lat: elel.latitude,
                      lng: elel.longitude,
                    }}
                    image={{
                      src:
                        getEventTypeKey(elel.event_type, t) === "vehicleRolloverEvent"
                          ? redmarker
                          : orangemarker,
                      size: {
                        width: 40,
                        height: 40,
                      },
                      options: {
                        offset: {
                          x: 20,
                          y: 20,
                        },
                      },
                    }}
                  />
                  {selectedEvent === elel.registered_at && (
                    <UnifiedOverlay
                      clickable={true}
                      position={{
                        lat: elel.latitude,
                        lng: elel.longitude,
                      }}
                      yAnchor={1.5}
                      xAnchor={0.5}
                      zIndex={theme.zindex.overlayMap}
                    >
                      <OverlayInnerStyledDiv>
                        <OverlayStatusStyledP status={getEventTypeKey(elel.event_type, t)}>
                          {getEventTypeValue(getEventTypeKey(elel.event_type, t))}(
                          {formatDate(elel.registered_at, "HH:mm")})
                        </OverlayStatusStyledP>
                        <OverlayAddressStyledDiv>
                          {eventAddressIsLoading && <DotLoader />}
                          {/* {eventAddressIsLoading && `로딩중...`} */}
                          {!eventAddressIsLoading && eventAddress}
                        </OverlayAddressStyledDiv>

                        <CopyContainerStyledDiv>
                          <div
                            style={{
                              display: "flex",
                              marginRight: "10px",
                            }}
                            onClick={() => {
                              copyToClipboard(eventAddress);
                              enqueueSnackbar(
                                `클립보드에 복사되었습니다.`,
                                {
                                  variant: "success",
                                }
                              );
                            }}
                          >
                            <p>복사</p>
                            <CopyButton />
                          </div>
                        </CopyContainerStyledDiv>

                        {
                          <BubbleStyledDiv>
                            <BubbleTailIcon height="25px" />
                          </BubbleStyledDiv>
                        }
                      </OverlayInnerStyledDiv>
                    </UnifiedOverlay>
                  )}
                </div>
              ))
          }
        </UnifiedMap>
      </WrapperStyledDiv>
    </>
  );
};

const WrapperStyledDiv = styled.div`
  flex: 1;
  width: 100%;
`;

const SpinnerContainerStyledDiv = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 15;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(231, 229, 228, 0.6);
`;

const ButtonsContainerStyledDiv = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  height: 70px;
  gap: 5px;
`;

const MarkerFilterStyledButton = styled.button<{
  isActive?: boolean;
  isBlock?: boolean;
}>`
  display: ${({ isBlock }) => (isBlock ? "flex" : "none")};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: transparent;
  border: none;
  transition: 200ms;
  filter: ${({ isActive }) =>
    isActive ? "grayscale(0)" : "grayscale(1)"};

  svg {
    cursor: pointer;
  }
  span {
    background-color: ${({ isActive }) =>
      isActive ? `${theme.color.red_100}` : `${theme.color.neutral_500}`};
    color: ${({ isActive }) =>
      isActive ? `${theme.color.neutral_500}` : `${theme.color.red_100}`};
    font-weight: ${({ isActive }) => isActive && "bold"};
    font-size: 11px;
    border-radius: 10px;
    cursor: pointer;
    padding-left: 5px;
    padding-right: 5px;
  }
  &:hover {
    scale: 110%;
  }
`;

const MapTypeToggleWrapperStyledDiv = styled.div`
  position: absolute;
  top: 20px;
  right: 30px;
  z-index: 99; /* 모달(98)보다 위에 표시 */
  cursor: pointer;
`;

const MapTypeBoxStyledDiv = styled.div<{
  isFirst: boolean;
  isSelected: boolean;
}>`
  width: 39px;
  height: 44px;
  // isFirst 왜 들어간거지 ?
  border-top-left-radius: ${({ isFirst }) => (isFirst ? "73px" : "0px")};
  border-top-right-radius: ${({ isFirst }) => (isFirst ? "73px" : "0px")};
  border-bottom-left-radius: ${({ isFirst }) =>
    isFirst ? "0px" : "73px"};
  border-bottom-right-radius: ${({ isFirst }) =>
    isFirst ? "0px" : "73px"};
  border-bottom: ${({ isFirst }) =>
    isFirst ? `1px solid ${theme.color.neutral_200}` : ""};
  background: ${theme.color.white};
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 700 : 400)};
  font-size: 13px;
  line-height: 15px;
  color: ${({ isSelected }) =>
    isSelected
      ? `${theme.color.neutral_800}`
      : `${theme.color.neutral_300}`};
  text-align: center;

  &:hover {
    background-color: ${theme.color.neutral_100};
  }
`;

const OverlayInnerStyledDiv = styled.div`
  width: 212px;
  padding-top: 18px;
  padding-bottom: 25px;
  padding-left: 15px;
  padding-right: 15px;
  background: ${theme.color.white};
  border-radius: 20px;
  cursor: default;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const OverlayStatusStyledP = styled.p<{ status: string }>`
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  color: ${({ status }) =>
    status === "vehicleRolloverEvent"
      ? `${theme.color.red_500}`
      : `${theme.color.orange_500}`};
`;

const OverlayAddressStyledDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 120%;
  text-align: center;
  min-height: 36px;
  color: ${theme.color.neutral_800};
  white-space: pre-line;
`;

const CopyContainerStyledDiv = styled.div`
  position: absolute;
  bottom: 5px;
  right: 10px;
  color: ${theme.statusColor.MAIN};
  display: flex;
  justify-content: flex-end;
  width: 100%;
  p {
    font-size: 11px;
    text-align: center;
    line-height: 20px;
  }

  div {
    padding: 5px;
    cursor: pointer;
    &:hover {
      background-color: ${theme.color.neutral_100};
      border-radius: 5px;
    }
  }
`;

const BubbleStyledDiv = styled.div`
  position: absolute;
  bottom: -25px;
  right: 50%;
  transform: translateX(50%);
  z-index: 10;

  img {
    width: 35px;
  }
`;
